import fg from "fast-glob";
import { createReadStream } from "fs";
import { stat } from "fs/promises";
import { basename } from "path";
import { createInterface } from "readline";
import { canonicalPath } from "../canonical-path";
import { getLogger } from "../logger";
import { codexNameSlotsFor } from "../name-stores";
import {
  applyNameCandidate,
  isCodexInjectedContext,
  type NameSlots,
  resolveSessionName,
} from "../session-name";
import { cleanSystemTags } from "../tags";
import type {
  ContentTier,
  Conversation,
  ConversationMessage,
  ConversationMeta,
  MessageSender,
  MessageSnapshot,
  ToolUseBlock,
} from "../types";
import {
  CODEX_CLI_PROVIDER,
  type DiscoveredConversationFile,
  type ScannerProvider,
} from "./provider";

// Serializable fold state for a Codex CLI rollout session. Plain JSON so a
// future persistent engine can resume it from a byte offset (same contract as
// the Threadbase ReducerState).
export interface CodexAccumulator {
  sessionId: string;
  cwd: string;
  gitBranch: string | null;
  model: string | null;
  latestTimestamp: string;
  messageCount: number;
  lastMessageSender: MessageSender;
  isSubagent: boolean;
  parentSessionUuid: string | null;
  firstUser: MessageSnapshot | null;
  nameSlots: NameSlots;
  lastUser: MessageSnapshot | null;
  lastAssistant: MessageSnapshot | null;
  toolNames: string[];
  previewParts: string[];
  previewLength: number;
  snippetParts: string[];
  snippetLength: number;
}

// Codex CLI rollout sessions live as rollout-*.jsonl under a date-partitioned
// tree. The Codex provider is opt-in: the scanner only discovers under roots the
// caller passes explicitly (no default home scan).
export class CodexCliProvider implements ScannerProvider<CodexAccumulator> {
  readonly name = CODEX_CLI_PROVIDER;

  async discover(roots: string[]): Promise<DiscoveredConversationFile[]> {
    const log = getLogger();
    const results: DiscoveredConversationFile[] = [];
    for (const root of roots) {
      let paths: string[];
      try {
        paths = await fg(["**/rollout-*.jsonl", "**/*.jsonl"], {
          cwd: root,
          absolute: true,
          dot: false,
          unique: true,
        });
      } catch (err) {
        log.warn({ root, err }, "codex discovery: glob failed");
        continue;
      }
      for (const filePath of paths) {
        try {
          const s = await stat(filePath);
          // Canonical form — fast-glob emits forward slashes even on Windows,
          // but every by-path lookup keys off the canonical spelling.
          if (s.size > 0) results.push({ filePath: canonicalPath(filePath), account: "codex" });
        } catch (err) {
          log.warn({ filePath, err }, "codex discovery: stat failed");
        }
      }
    }
    return results;
  }

  // Codex rollout lines carry distinctive top-level types.
  canParse(_filePath: string, sample: string): boolean {
    for (const line of sample.split("\n")) {
      if (!line.trim()) continue;
      try {
        const e = JSON.parse(line) as Record<string, unknown>;
        if (e.type === "session_meta" || e.type === "response_item" || e.type === "event_msg") {
          return true;
        }
        if (e.type === "user" || e.type === "assistant") return false;
      } catch {}
    }
    return false;
  }

  createEmptyAccumulator(): CodexAccumulator {
    return {
      sessionId: "",
      cwd: "",
      gitBranch: null,
      model: null,
      latestTimestamp: "",
      messageCount: 0,
      lastMessageSender: "user",
      isSubagent: false,
      parentSessionUuid: null,
      firstUser: null,
      nameSlots: {},
      lastUser: null,
      lastAssistant: null,
      toolNames: [],
      previewParts: [],
      previewLength: 0,
      snippetParts: [],
      snippetLength: 0,
    };
  }

  reduceEntry(acc: CodexAccumulator, entry: Record<string, unknown>, tier: ContentTier): void {
    reduceCodexEntry(acc, entry, tier);
  }

  finalize(
    acc: CodexAccumulator,
    filePath: string,
    account: string,
    tier: ContentTier,
  ): ConversationMeta | null {
    return finalizeCodexMeta(acc, filePath, account, tier);
  }
}

// ─── Reducer (exported for tests / future incremental engine) ───────────

const asString = (v: unknown): string => (typeof v === "string" ? v : "");

// Pull display text out of a Codex content array of {type,text} blocks.
function extractCodexText(content: unknown): string {
  if (typeof content === "string") return cleanSystemTags(content);
  if (!Array.isArray(content)) return "";
  return content
    .map((item) => {
      if (typeof item === "string") return item;
      const t = item?.type;
      if ((t === "input_text" || t === "output_text" || t === "text") && item?.text) {
        return item.text as string;
      }
      return "";
    })
    .filter(Boolean)
    .map(cleanSystemTags)
    .join(" ");
}

// Map one raw Codex rollout JSONL line to the message it renders as, or null
// when it renders as nothing (session_meta, event_msg, encrypted reasoning,
// developer/system roles, an empty-after-cleanSystemTags body, malformed JSON).
// Stateless — a Codex line carries everything the decision needs — and tolerant
// by construction: it never throws. parseCodexConversation() below delegates to
// it so the "which lines render as messages" rule has exactly one definition.
export function parseCodexJsonlLine(line: string): ConversationMessage | null {
  if (!line.trim()) return null;
  let entry: Record<string, unknown>;
  try {
    entry = JSON.parse(line);
  } catch {
    return null;
  }
  return codexEntryToMessage(entry);
}

// Same decision, on an already-parsed entry — lets parseCodexConversation reuse
// its single JSON.parse (it also needs session_meta and the timestamp).
//
// Tool calls, their outputs and reasoning summaries render too, in the shape the
// Claude parser gives them (toolUseBlocks / toolResults / thinkingContent with
// empty text), so a host renders every provider with one mapper. `uuid` is the
// rollout item id, which is also what a host's live stream keys the line on.
function codexEntryToMessage(entry: Record<string, unknown>): ConversationMessage | null {
  const payload = entry.payload as Record<string, unknown> | undefined;
  if (!payload || typeof payload !== "object") return null;
  if (entry.type !== "response_item") return null;
  const timestamp = asString(entry.timestamp);
  const uuid = asString(payload.id) || undefined;
  const base = { timestamp, ...(uuid ? { uuid } : {}) };

  switch (payload.type) {
    case "message": {
      const role = payload.role;
      if (role !== "user" && role !== "assistant") return null;
      const text = extractCodexText(payload.content);
      if (!text) return null;
      return { role: role as MessageSender, text, ...base };
    }
    case "function_call":
    case "custom_tool_call":
    case "web_search_call": {
      const block = codexToolUseBlock(payload);
      if (!block) return null;
      return {
        role: "assistant",
        text: "",
        ...base,
        metadata: { toolUses: [block.name], toolUseBlocks: [block] },
      };
    }
    case "function_call_output":
    case "custom_tool_call_output": {
      const toolUseId = asString(payload.call_id);
      if (!toolUseId) return null;
      return {
        role: "user",
        text: "",
        ...base,
        isToolResult: true,
        metadata: {
          toolResults: [
            {
              toolUseId,
              type: "generic",
              content: { output: codexToolOutputText(payload.output) },
            },
          ],
        },
      };
    }
    case "reasoning": {
      // Almost always encrypted_content with an empty summary; only a readable
      // summary renders.
      const summary = Array.isArray(payload.summary) ? payload.summary : [];
      const thinkingContent = summary
        .map((s) => asString((s as Record<string, unknown>)?.text))
        .filter((t) => t.trim())
        .join("\n\n");
      if (!thinkingContent) return null;
      return { role: "assistant", text: "", ...base, isThinking: true, thinkingContent };
    }
    default:
      return null;
  }
}

function codexToolUseBlock(payload: Record<string, unknown>): ToolUseBlock | null {
  if (payload.type === "web_search_call") {
    const action = payload.action;
    return {
      id: asString(payload.id),
      name: "web_search",
      input: action && typeof action === "object" ? (action as Record<string, unknown>) : {},
    };
  }
  const name = asString(payload.name);
  if (!name) return null;
  const id = asString(payload.call_id);
  if (payload.type === "custom_tool_call") {
    return { id, name, input: { input: asString(payload.input) } };
  }
  const args = asString(payload.arguments);
  let input: Record<string, unknown> = { arguments: args };
  try {
    const parsed = JSON.parse(args);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) input = parsed;
  } catch {
    // Not JSON — keep the raw string.
  }
  return { id, name, input };
}

// Tool output is a plain string or an array of {type: input_text, text} parts.
// Not run through cleanSystemTags: it is program output, shown verbatim.
function codexToolOutputText(output: unknown): string {
  if (typeof output === "string") return output;
  if (!Array.isArray(output)) return "";
  return output
    .map((item) => (typeof item === "string" ? item : asString(item?.text)))
    .filter(Boolean)
    .join("\n");
}

// Fold one Codex rollout line into the accumulator. Tolerant by construction:
// any line it doesn't recognise is ignored, never thrown on.
export function reduceCodexEntry(
  acc: CodexAccumulator,
  entry: Record<string, unknown>,
  tier: ContentTier,
): void {
  const ts = asString(entry.timestamp);
  if (ts && (!acc.latestTimestamp || ts > acc.latestTimestamp)) acc.latestTimestamp = ts;

  const payload = entry.payload as Record<string, unknown> | undefined;
  if (!payload || typeof payload !== "object") return;

  const type = entry.type as string;

  if (type === "session_meta") {
    if (!acc.sessionId) acc.sessionId = asString(payload.id);
    if (!acc.cwd) acc.cwd = asString(payload.cwd);
    const git = payload.git as Record<string, unknown> | undefined;
    if (acc.gitBranch === null && git?.branch) acc.gitBranch = asString(git.branch) || null;
    if (!acc.isSubagent && isSubagentSource(payload.source)) {
      acc.isSubagent = true;
      acc.parentSessionUuid = parentThreadId(payload.source);
    }
    return;
  }

  // Model lives on turn_context records (and occasionally elsewhere).
  if (acc.model === null && payload.model) acc.model = asString(payload.model) || null;

  if (type !== "response_item") return;

  const ptype = payload.type as string;

  // Tool calls — collect the tool name.
  if (ptype === "function_call" || ptype === "custom_tool_call") {
    const name = asString(payload.name);
    if (name && !acc.toolNames.includes(name)) acc.toolNames.push(name);
    return;
  }

  if (ptype !== "message") return;

  const role = payload.role;
  // developer/system/tool roles carry sandbox boilerplate — not user-visible turns.
  if (role !== "user" && role !== "assistant") return;

  const text = extractCodexText(payload.content);
  if (!text) return;

  // Codex prepends its AGENTS.md / sandbox preamble as the first `user` turn, so
  // the role guard above cannot catch it: same category, wrong label. Counting it
  // makes every conversation's preview and firstMessage that blob.
  //
  // Bounded to LEADING turns on purpose. A human pasting instruction text
  // mid-conversation would otherwise go silently uncounted, and messageCount is a
  // number people compare against what they can see. Codex only injects at the
  // head, so the bound costs nothing real.
  if (acc.messageCount === 0 && role === "user" && isCodexInjectedContext(text)) return;

  const sender = role as MessageSender;
  acc.messageCount++;
  acc.lastMessageSender = sender;

  const snapshot: MessageSnapshot = { text: text.slice(0, 200), timestamp: ts };
  if (sender === "user") {
    if (!acc.firstUser) acc.firstUser = snapshot;
    acc.nameSlots = applyNameCandidate(acc.nameSlots, "firstUserMessage", text, CODEX_CLI_PROVIDER);
    acc.lastUser = snapshot;
  } else {
    acc.lastAssistant = snapshot;
  }

  if (acc.previewLength < tier.previewMax) {
    acc.previewParts.push(text);
    acc.previewLength += text.length;
  }
  if (acc.snippetLength < tier.snippetMax) {
    const remaining = tier.snippetMax - acc.snippetLength;
    const chunk = text.length > remaining ? text.slice(0, remaining) : text;
    acc.snippetParts.push(chunk);
    acc.snippetLength += chunk.length;
  }
}

export function finalizeCodexMeta(
  acc: CodexAccumulator,
  filePath: string,
  account: string,
  tier: ContentTier,
): ConversationMeta | null {
  if (acc.messageCount === 0) return null;

  const sessionId = acc.sessionId || basename(filePath, ".jsonl");
  const nameSlots = { ...acc.nameSlots, ...codexNameSlotsFor(filePath, acc.sessionId) };
  const projectPath = acc.cwd;
  // Treat a session with tool calls but no plain back-and-forth as task-shaped.
  const kind: "conversation" | "task" =
    acc.lastAssistant === null && acc.toolNames.length > 0 ? "task" : "conversation";

  return {
    id: filePath,
    filePath,
    provider: CODEX_CLI_PROVIDER,
    kind,
    externalSessionId: acc.sessionId || undefined,
    sessionId,
    sessionName: resolveSessionName(nameSlots).name ?? "",
    nameSlots,
    projectPath,
    projectName: getShortProjectName(projectPath),
    account,
    timestamp: acc.latestTimestamp || new Date().toISOString(),
    messageCount: acc.messageCount,
    lastMessageSender: acc.lastMessageSender,
    preview: acc.previewParts.join(" ").slice(0, tier.previewMax),
    contentSnippet: acc.snippetParts.join(" "),
    gitBranch: acc.gitBranch,
    model: acc.model,
    isSubagent: acc.isSubagent,
    // Codex exposes no path-shaped parent, and no per-child id of its own.
    parentSessionId: null,
    parentSessionUuid: acc.parentSessionUuid ?? undefined,
    isTeammate: false,
    teamName: null,
    toolNames: acc.toolNames,
    firstMessage: acc.firstUser,
    lastMessage: acc.lastAssistant ?? acc.lastUser,
    lastPrompt: acc.lastUser?.text || undefined,
  };
}

export { isCodexInjectedContext };

function getShortProjectName(fullPath: string): string {
  return fullPath.split("/").filter(Boolean).slice(-3).join("/");
}

// `session_meta.payload.source` is either a plain string for a top-level session
// ("cli", "vscode", "exec", …) or an object naming how the child was spawned.
// The `subagent` VALUE is not always a dict — {"subagent":"review"} is a real
// shape on disk alongside {"subagent":{"thread_spawn":{…}}} and
// {"subagent":{"other":"guardian"}} — so presence of the key is the test, not
// its shape. Unknown string sources are treated as ordinary sessions rather
// than failing, so a future Codex adds a source without breaking the scan.
function isSubagentSource(source: unknown): boolean {
  return typeof source === "object" && source !== null && "subagent" in source;
}

// Only thread_spawn names a parent. A subagent without a discoverable parent is
// still a subagent — it just has no parent id to report.
function parentThreadId(source: unknown): string | null {
  if (!isSubagentSource(source)) return null;
  const subagent = (source as { subagent: unknown }).subagent;
  if (typeof subagent !== "object" || subagent === null) return null;
  const spawn = (subagent as Record<string, unknown>).thread_spawn;
  if (typeof spawn !== "object" || spawn === null) return null;
  return asString((spawn as Record<string, unknown>).parent_thread_id) || null;
}

// Full conversation parse for a Codex rollout session — the Codex analogue of
// parseConversation(). Streams the file, emitting one ConversationMessage per
// user/assistant message item. Used by getConversation()/getConversationPage()
// when the resolved meta is a codex-cli conversation.
export async function parseCodexConversation(
  filePath: string,
  account: string,
): Promise<Conversation | null> {
  const log = getLogger();
  const messages: ConversationMessage[] = [];
  const textParts: string[] = [];
  let sessionId = "";
  let cwd = "";
  let latestTimestamp = "";
  let lastUserText = "";
  let nameSlots: NameSlots = {};

  const rl = createInterface({ input: createReadStream(filePath), crlfDelay: Infinity });
  try {
    for await (const line of rl) {
      if (!line.trim()) continue;
      let entry: Record<string, unknown>;
      try {
        entry = JSON.parse(line);
      } catch {
        continue;
      }
      const ts = asString(entry.timestamp);
      if (ts && (!latestTimestamp || ts > latestTimestamp)) latestTimestamp = ts;
      const payload = entry.payload as Record<string, unknown> | undefined;
      if (!payload || typeof payload !== "object") continue;

      if (entry.type === "session_meta") {
        if (!sessionId) sessionId = asString(payload.id);
        if (!cwd) cwd = asString(payload.cwd);
        continue;
      }
      const message = codexEntryToMessage(entry);
      if (!message) continue;
      messages.push(message);
      // Tool and thinking messages carry no text; they must not blank lastPrompt.
      if (!message.text) continue;
      textParts.push(message.text);
      if (message.role === "user") {
        lastUserText = message.text;
        nameSlots = applyNameCandidate(
          nameSlots,
          "firstUserMessage",
          message.text,
          CODEX_CLI_PROVIDER,
        );
      }
    }
  } catch (err) {
    log.warn({ filePath, err }, "parseCodexConversation: read failed");
    return null;
  }

  if (messages.length === 0) return null;

  return {
    id: filePath,
    filePath,
    projectPath: cwd,
    projectName: getShortProjectName(cwd),
    sessionId: sessionId || basename(filePath, ".jsonl"),
    sessionName:
      resolveSessionName({ ...nameSlots, ...codexNameSlotsFor(filePath, sessionId) }).name ?? "",
    messages,
    fullText: textParts.join(" "),
    timestamp: latestTimestamp || new Date().toISOString(),
    messageCount: messages.length,
    account,
    lastPrompt: lastUserText || undefined,
  };
}
